import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SiteTitleContext = createContext();

export const useSiteTitle = () => {
  const context = useContext(SiteTitleContext);
  if (!context) {
    throw new Error('useSiteTitle must be used within a SiteTitleProvider');
  }
  return context;
};

export const SiteTitleProvider = ({ children }) => {
  const [siteTitle, setSiteTitle] = useState({ part1: 'YEGO', part2: '' });
  const [loading, setLoading] = useState(true);

  // Load site title from localStorage and backend
  useEffect(() => {
    const loadSiteTitle = async () => {
      // Clear any old cached values that might contain KG Vibes
      const cached = localStorage.getItem('site_title');
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          // If old KG Vibes values exist, clear them
          if (parsed.part1 && (parsed.part1.toLowerCase().includes('kg') || parsed.part1.toLowerCase().includes('vibes'))) {
            localStorage.removeItem('site_title');
          } else {
            setSiteTitle(parsed);
          }
        } catch (e) {
          localStorage.removeItem('site_title');
        }
      }

      // Then try to fetch from backend
      try {
        const response = await axios.get(`${BACKEND_URL}/api/settings/site-title`);
        if (response.data.title) {
          const newTitle = response.data.title;
          // Validate that it's not old KG Vibes values
          if (newTitle.part1 && !newTitle.part1.toLowerCase().includes('kg') && !newTitle.part1.toLowerCase().includes('vibes')) {
            setSiteTitle(newTitle);
            localStorage.setItem('site_title', JSON.stringify(newTitle));
          } else {
            // Force YEGO branding
            const yegoTitle = { part1: 'YEGO', part2: '' };
            setSiteTitle(yegoTitle);
            localStorage.setItem('site_title', JSON.stringify(yegoTitle));
          }
        }
      } catch (error) {
        // Use default YEGO
        setSiteTitle({ part1: 'YEGO', part2: '' });
      } finally {
        setLoading(false);
      }
    };

    loadSiteTitle();
  }, []);

  // Save site title
  const updateSiteTitle = async (newTitle) => {
    try {
      const token = localStorage.getItem('yegoNewsToken');
      await axios.post(`${BACKEND_URL}/api/settings/site-title`, newTitle, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSiteTitle(newTitle);
      localStorage.setItem('site_title', JSON.stringify(newTitle));
      return true;
    } catch (error) {
      console.error('Failed to save site title:', error);
      // Still update locally
      setSiteTitle(newTitle);
      localStorage.setItem('site_title', JSON.stringify(newTitle));
      return false;
    }
  };

  // Get full title as string
  const getFullTitle = () => {
    return `${siteTitle.part1}${siteTitle.part2}`;
  };

  return (
    <SiteTitleContext.Provider value={{ 
      siteTitle, 
      updateSiteTitle, 
      getFullTitle,
      loading 
    }}>
      {children}
    </SiteTitleContext.Provider>
  );
};

export default SiteTitleContext;
